import { type ReactElement, useMemo } from 'react'

import { MuxStoryboardFrameStrip } from 'components/agents/MuxStoryboardFrameStrip'
import styles from 'components/agents/VideoSegmentStrip.module.css'
import { timestampToSeconds } from 'utils/frameRefinement'
import { buildMuxStoryboardUrl } from 'utils/muxStoryboard'
import { PERCENT_SCALE } from 'utils/units'

import type { MuxPlaybackEmbed } from 'utils/videoUrlResolver'

interface VideoSegmentStripProperties {
  mux?: MuxPlaybackEmbed
  startTime: string
  endTime: string
  frameCount?: number
  videoDuration?: number
  currentPlaybackTime?: number
}

const DEFAULT_FRAME_COUNT = 7

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value))

interface VideoSegmentStripGeometry {
  startPercent: number
  endPercent: number
  startSeconds: number
  endSeconds: number
  playheadPercent: number
  showPlaybackIndicator: boolean
}

export const getVideoSegmentStripGeometry = ({
  duration,
  startTime,
  endTime,
  currentPlaybackTime,
}: {
  duration?: number
  startTime: string
  endTime: string
  currentPlaybackTime?: number
}): VideoSegmentStripGeometry => {
  if (!duration || !Number.isFinite(duration) || duration <= 0) {
    return {
      startPercent: 0,
      endPercent: PERCENT_SCALE,
      startSeconds: 0,
      endSeconds: 0,
      playheadPercent: 0,
      showPlaybackIndicator: false,
    }
  }

  const start = timestampToSeconds(startTime)
  const end = timestampToSeconds(endTime)
  const safeStart = clamp(start, 0, duration)
  const safeEnd = clamp(Math.max(end, safeStart), 0, duration)
  const startPercent = clamp((safeStart / duration) * PERCENT_SCALE, 0, PERCENT_SCALE)
  const endPercent = clamp((safeEnd / duration) * PERCENT_SCALE, 0, PERCENT_SCALE)
  const segmentDuration = safeEnd - safeStart
  const showPlaybackIndicator =
    currentPlaybackTime !== undefined && currentPlaybackTime >= safeStart && currentPlaybackTime <= safeEnd

  if (currentPlaybackTime === undefined || segmentDuration <= 0) {
    return {
      startPercent,
      endPercent,
      startSeconds: safeStart,
      endSeconds: safeEnd,
      playheadPercent: startPercent,
      showPlaybackIndicator,
    }
  }

  const relativeClipPercent = clamp(
    ((currentPlaybackTime - safeStart) / segmentDuration) * PERCENT_SCALE,
    0,
    PERCENT_SCALE
  )
  const playheadPercent = clamp(
    startPercent + (relativeClipPercent / PERCENT_SCALE) * (endPercent - startPercent),
    0,
    PERCENT_SCALE
  )

  return {
    startPercent,
    endPercent,
    startSeconds: safeStart,
    endSeconds: safeEnd,
    playheadPercent,
    showPlaybackIndicator,
  }
}

export const VideoSegmentStrip = ({
  mux,
  startTime,
  endTime,
  frameCount = DEFAULT_FRAME_COUNT,
  videoDuration,
  currentPlaybackTime,
}: VideoSegmentStripProperties): ReactElement | null => {
  /* --- Section 3: Derived Values --- */
  const { startPercent, endPercent, playheadPercent, showPlaybackIndicator } = useMemo(() => {
    return getVideoSegmentStripGeometry({
      duration: videoDuration,
      startTime,
      endTime,
      currentPlaybackTime,
    })
  }, [videoDuration, startTime, endTime, currentPlaybackTime])

  const selectionWidth = Math.max(endPercent - startPercent, 0)
  const hasMuxVisuals = Boolean(buildMuxStoryboardUrl(mux) ?? mux?.thumbnailUrl)

  /* --- Section 5: Early Returns --- */
  if (!hasMuxVisuals) {
    return null
  }

  /* --- Section 6: Render --- */
  return (
    <div className={styles.strip}>
      <div className={styles.track}>
        <MuxStoryboardFrameStrip
          className={styles.frames}
          durationSeconds={videoDuration}
          fallbackImageClassName={styles.frame}
          frameClassName={styles.frame}
          frameCount={frameCount}
          mode="even"
          mux={mux}
        />
        <svg className={styles.overlay} viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
          {startPercent > 0 ? <rect className={styles.dim} x="0" y="0" width={startPercent} height="100" /> : null}
          {endPercent < PERCENT_SCALE ? (
            <rect className={styles.dim} x={endPercent} y="0" width={PERCENT_SCALE - endPercent} height="100" />
          ) : null}
          <rect className={styles.selection} x={startPercent} y="0" width={selectionWidth} height="100" />
          {showPlaybackIndicator ? (
            <line className={styles.playhead} x1={playheadPercent} x2={playheadPercent} y1="0" y2="100" />
          ) : null}
        </svg>
      </div>
    </div>
  )
}
